// One group of resources — the list page behind every card on the Resources
// landing page.
//
// Phase 7 stage 4 (docs/phase-7-parity.md) built this as the single Resources
// screen: sortable columns, real filters, a search box, status as a badge
// rather than a word, an overflow menu per row, CSV import — and Status Today,
// which is the single most useful thing on the page.
//
// Parity pass 2 batch 1 turned it into a GROUP list. Six kinds is too many for
// one table with a dropdown, so the page now takes a group (People, Assets,
// Subcontractors, Suppliers, Other — see lib/resourceGroups.ts) and shows only
// that. Assets is two kinds at once, plant and vehicle, which is why the Kind
// column and the kind filter only appear when a group actually has more than
// one kind in it.
//
// STATUS TODAY is derived from the live schedule, not stored on the row. The
// stored status answers "what did someone last set this to"; Status Today
// answers "where is this thing right now", which is the question anyone
// actually opens this page to ask.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  scheduling,
  type CalendarBooking,
  type Resource,
  type ResourceKind,
  type Unavailability,
} from '../schedulingApi';
import { api, ApiError, type User } from '../api';
import { formatDMY, todayISO } from '../lib/dates';
import {
  groupByKey,
  KIND_LABEL,
  type ResourceGroup,
  type ResourceGroupKey,
} from '../lib/resourceGroups';
import {
  ActionMenu,
  CardList,
  CsvImport,
  EmptyState,
  LinkedRow,
  Modal,
  PageHeader,
  RowCard,
  SortHeader,
  StatusBadge,
  type CsvRow,
  type SortDirection,
} from '../components/ui';
import { Field } from '../ui';
import { useLatestRequest } from '../lib/useLatestRequest';

export { KIND_LABEL };

type SortKey = 'name' | 'kind' | 'category' | 'status' | 'today' | 'identifier' | 'rego';

/**
 * What a resource is doing today, in precedence order.
 *
 * Archived and unavailable beat everything, because they are statements about
 * the resource itself rather than about its diary. Away beats booked: a machine
 * that is booked AND under service is a problem someone needs to see, and
 * showing "on a job" would hide it. That precedence is the whole reason this is
 * a function with a comment rather than a ternary.
 */
function statusToday(
  resource: Resource,
  bookedIds: Set<string>,
  awayIds: Set<string>
): 'archived' | 'unavailable' | 'away' | 'in_use' | 'available' {
  if (resource.status === 'archived') return 'archived';
  if (resource.status === 'unavailable') return 'unavailable';
  if (awayIds.has(resource.id)) return 'away';
  if (bookedIds.has(resource.id)) return 'in_use';
  return 'available';
}

/**
 * The import template, per group. Only the columns that group can actually
 * hold: handing someone importing a crew list a `registrationNumber` column
 * they must leave blank is how a template stops being an example.
 */
function templateCsv(group: ResourceGroup): string {
  if (group.key === 'assets') {
    return [
      'name,kind,identifier,registrationNumber,registrationExpiry,category,notes',
      '5T Excavator,plant,EX-05,,,Excavator,Services every 250 hours',
      'Tipper truck,vehicle,TRK-12,XT17AZ,2027-03-31,Truck,',
      '',
    ].join('\n');
  }
  // People's column is headed `position` (round 37 — "category" reads as
  // de-humanising on a crew list). The import mapper accepts either spelling,
  // so a template downloaded before the rename still imports.
  return [
    group.key === 'people' ? 'name,position,notes' : 'name,category,notes',
    group.key === 'people' ? 'Jack Miller,Excavator operator,' : `Example ${group.label},,`,
    '',
  ].join('\n');
}

export function ResourcesPage({ group: groupKey }: { group: ResourceGroupKey }) {
  const today = todayISO();
  const group = groupByKey(groupKey);
  /** Assets is plant AND vehicle; every other group is a single kind. */
  const multiKind = group.kinds.length > 1;
  const isAssets = group.key === 'assets';
  /**
   * Only a person-kind resource can hold a user account —
   * `assertUserLinkable` on the server refuses every other kind — so on
   * Assets, Subcontractors, Suppliers and Other the Linked account column
   * could only ever be a column of dashes. The phone card already worked
   * this way: it emits "Linked to ..." only when there is a link.
   */
  const linkable = group.kinds.includes('person');
  /**
   * "Position" for people, "Category" for everything else (round 37, Nathan's
   * word: a crew list headed Category reads as de-humanising). The FIELD is
   * still `category` — this is a label, not a schema change — and "Position"
   * rather than "Role" because a role in Dayboard is already admin / manager /
   * field worker, and one word carrying both ideas is the ambiguity round 30
   * renamed People → Users to avoid.
   */
  const categoryLabel = group.key === 'people' ? 'Position' : 'Category';

  const [resources, setResources] = useState<Resource[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [todayBookings, setTodayBookings] = useState<CalendarBooking[]>([]);
  const [todayAway, setTodayAway] = useState<Unavailability[]>([]);

  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  /**
   * Who has something lapsing. People only, and asked of the SERVER because
   * the window is the company's setting rather than anything the row carries —
   * the ids come back and narrow the list already on screen.
   */
  const [needsAttention, setNeedsAttention] = useState(false);
  const [attentionIds, setAttentionIds] = useState<Set<string> | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const begin = useLatestRequest();

  const load = useCallback(async () => {
    // Switching resource group re-runs this; only the newest may land. See
    // `useLatestRequest`.
    const isCurrent = begin();
    try {
      // Asked for unfiltered and narrowed here rather than through the
      // server's kind filter: Assets is two kinds in one list and that filter
      // takes one. A few dozen rows either way, at this product's scale.
      const [list, booked, unavailable, attention] = await Promise.all([
        // Archived ones INCLUDED, and then hidden here by the "Show archived"
        // checkbox. The server excludes them by default, so asking plainly —
        // as this page did until 2026-09-06 — meant the archived rows never
        // reached the browser: the checkbox filtered a list that could not
        // contain what it was filtering for, and its own `archivedCount` was
        // therefore always 0, which kept the checkbox itself off the screen.
        // Archiving an excavator made it vanish with no way back to it from
        // this page at all. A client-side filter has to be given the rows.
        scheduling.listResources({ includeArchived: true }),
        scheduling.calendar({ from: today, to: today }),
        scheduling.unavailabilityIn({ from: today, to: today }),
        // Only the People group has records to be behind on, so nothing else
        // pays for this request.
        group.key === 'people'
          ? scheduling.listResources({ kind: 'person', needsAttention: true })
          : Promise.resolve(null),
      ]);
      if (!isCurrent()) return;
      setResources(list.resources.filter((r) => group.kinds.includes(r.kind)));
      setAttentionIds(
        attention === null ? null : new Set(attention.resources.map((r) => r.id))
      );
      setTodayBookings(booked.bookings);
      setTodayAway(unavailable.unavailability);
      setError(null);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof ApiError ? err.message : 'Could not load the resources.');
    }
  }, [begin, group.key, group.kinds, today]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // Only admins can list users; a manager simply gets no link picker.
    void api
      .listUsers()
      .then((r) => setUsers(r.users))
      .catch(() => setUsers([]));
  }, []);

  const bookedIds = useMemo(
    () => new Set(todayBookings.map((b) => b.resourceId)),
    [todayBookings]
  );
  const awayIds = useMemo(() => new Set(todayAway.map((a) => a.resourceId)), [todayAway]);

  const sortRows = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();

    const filtered = resources
      .filter((r) => showArchived || r.status !== 'archived')
      .filter((r) => !needsAttention || attentionIds?.has(r.id) === true)
      .filter((r) => kind === '' || r.kind === kind)
      .filter(
        (r) =>
          q === '' ||
          r.name.toLowerCase().includes(q) ||
          (r.category ?? '').toLowerCase().includes(q) ||
          // The identifier and the plate are both things someone searches by
          // when they are standing next to the machine reading it off the side.
          (r.identifier ?? '').toLowerCase().includes(q) ||
          (r.registrationNumber ?? '').toLowerCase().includes(q)
      );

    const value = (r: Resource): string => {
      switch (sortKey) {
        case 'name':
          return r.name.toLowerCase();
        case 'kind':
          return KIND_LABEL[r.kind];
        case 'category':
          // Uncategorised sorts last in either direction rather than first in
          // one of them, which is where a plain empty string would put it.
          return r.category?.toLowerCase() ?? 'zzzz';
        case 'status':
          return r.status;
        case 'today':
          return statusToday(r, bookedIds, awayIds);
        // Blank sorts last in either direction, same reason as category.
        case 'identifier':
          return r.identifier?.toLowerCase() ?? 'zzzz';
        case 'rego':
          return r.registrationNumber?.toLowerCase() ?? 'zzzz';
      }
    };

    return [...filtered].sort((a, b) => {
      const compared = value(a).localeCompare(value(b));
      return sortDir === 'asc' ? compared : -compared;
    });
  }, [
    resources,
    search,
    kind,
    showArchived,
    needsAttention,
    attentionIds,
    sortKey,
    sortDir,
    bookedIds,
    awayIds,
  ]);

  const onImport = async (rows: CsvRow[]): Promise<number> => {
    const result = await scheduling.importResources(
      rows.map((r) => ({
        name: r.name ?? '',
        // A file imported from inside a group defaults to that group's kind,
        // so a crew list with no kind column lands as people rather than as
        // whatever the server's global default happens to be.
        kind: r.kind || group.defaultKind,
        category: r.category || undefined,
        notes: r.notes || undefined,
        identifier: r.identifier || undefined,
        registrationNumber: r.registrationNumber || undefined,
        registrationExpiry: r.registrationExpiry || undefined,
      }))
    );
    await load();
    return result.imported;
  };

  const archivedCount = resources.filter((r) => r.status === 'archived').length;

  return (
    <>
      <PageHeader
        title={group.label}
        subtitle={group.blurb}
        actions={
          <>
            <CsvImport
              entityLabel={group.label.toLowerCase()}
              templateName={`dayboard-${group.path}-template.csv`}
              templateCsv={templateCsv(group)}
              requiredHeaders={['name']}
              previewColumns={
                isAssets
                  ? [
                      { key: 'name', label: 'Name' },
                      { key: 'kind', label: 'Kind' },
                      { key: 'identifier', label: 'ID' },
                      { key: 'registrationNumber', label: 'Rego' },
                      { key: 'registrationExpiry', label: 'Rego expiry' },
                      { key: 'category', label: 'Category' },
                    ]
                  : [
                      { key: 'name', label: 'Name' },
                      { key: 'category', label: categoryLabel },
                      { key: 'notes', label: 'Notes' },
                    ]
              }
              // A header is a parse key: templates downloaded before the
              // rename say `category`, and must keep importing.
              aliases={{ position: 'category' }}
              onImport={onImport}
            />
            <button type="button" className="btn-primary btn-sm" onClick={() => setAdding(true)}>
              Add {group.key === 'people' ? 'person' : group.label.toLowerCase().replace(/s$/, '')}
            </button>
          </>
        }
      />

      <p className="mb-4 text-sm">
        <Link to="/app/resources" className="table-link">
          &larr; All resources
        </Link>
      </p>

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-56">
          <label className="form-label" htmlFor="resource-search">
            Search
          </label>
          <input
            id="resource-search"
            type="search"
            className="form-input"
            placeholder={
              isAssets ? 'Search by name, ID or rego…' : 'Search by name or category…'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Only Assets holds more than one kind, so every other group would be
            offering a filter with a single option in it. */}
        {multiKind && (
          <div>
            <label className="form-label" htmlFor="resource-kind">
              Kind
            </label>
            <select
              id="resource-kind"
              className="form-select"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="">All</option>
              {group.kinds.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* No expiry COLUMN to go with it, deliberately: Zanetto Tools built
            the rollup onto its roster first, found it disagreeing on screen
            with the settings-driven reminders, and deleted it. */}
        {attentionIds !== null && attentionIds.size > 0 && (
          <label className="check-label-inline">
            <input
              type="checkbox"
              className="form-check"
              checked={needsAttention}
              onChange={(e) => setNeedsAttention(e.target.checked)}
            />
            Needs attention ({attentionIds.size})
          </label>
        )}

        {/* ALWAYS on screen, count and all — never gated on there being
            something to show (Nathan, 2026-09-06). A filter that appears only
            once it has work to do is a filter nobody can find on purpose: the
            moment you go looking for it is the moment you have just archived
            something and want to know where it went, and "(0)" answers that
            question honestly where an absent checkbox answers nothing. The
            needs-attention filter beside it stays conditional — that one is a
            People-only prompt about work outstanding, not a way back to a row
            the page is hiding. */}
        <label className="check-label-inline">
          <input
            type="checkbox"
            className="form-check"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived ({archivedCount})
        </label>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title={
            resources.length === 0 ? `No ${group.label.toLowerCase()} yet` : 'Nothing matches that'
          }
          body={
            resources.length === 0
              ? `${group.blurb} You can import a list from a spreadsheet instead of typing them all in.`
              : 'Try a different search, or clear the filters.'
          }
          action={
            resources.length === 0 ? (
              <button type="button" className="btn-primary btn-sm" onClick={() => setAdding(true)}>
                Add the first one
              </button>
            ) : undefined
          }
        />
      ) : (
        <>
        <CardList>
          {visible.map((resource) => {
            const today = statusToday(resource, bookedIds, awayIds);
            const linked = resource.linkedUserId
              ? (users.find((u) => u.id === resource.linkedUserId)?.name ?? 'Linked')
              : null;
            return (
              <RowCard
                key={resource.id}
                to={`/app/resources/${resource.id}`}
                title={resource.name}
                badges={
                  <>
                    <StatusBadge kind="today" status={today} />
                    {/* The stored status only earns a second badge when it
                        disagrees with a quiet "available" — repeating what
                        Today already says is noise. */}
                    {resource.status !== 'available' && (
                      <StatusBadge kind="resource" status={resource.status} />
                    )}
                    {/* Renders nothing at all when there is no expiry, which
                        is most rows. */}
                    <StatusBadge kind="expiry" status={resource.registrationExpiryStatus} />
                  </>
                }
              >
                <div>
                  {multiKind ? KIND_LABEL[resource.kind] : null}
                  {multiKind && resource.category ? ' · ' : ''}
                  {resource.category ?? (multiKind ? '' : '—')}
                </div>
                {resource.identifier && <div>ID {resource.identifier}</div>}
                {resource.registrationNumber && <div>Rego {resource.registrationNumber}</div>}
                {resource.soldDate && (
                  <div>
                    Sold {formatDMY(resource.soldDate)}
                    {resource.soldTo ? ` to ${resource.soldTo}` : ''}
                  </div>
                )}
                {linked && <div>Linked to {linked}</div>}
              </RowCard>
            );
          })}
        </CardList>
        <div className="table-wrap max-sm:hidden">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader
                  label="Name"
                  sortKey="name"
                  current={sortKey}
                  dir={sortDir}
                  onSort={sortRows}
                />
                {multiKind && (
                  <SortHeader
                    label="Kind"
                    sortKey="kind"
                    current={sortKey}
                    dir={sortDir}
                    onSort={sortRows}
                  />
                )}
                {isAssets && (
                  <>
                    <SortHeader
                      label="ID"
                      sortKey="identifier"
                      current={sortKey}
                      dir={sortDir}
                      onSort={sortRows}
                    />
                    <SortHeader
                      label="Rego"
                      sortKey="rego"
                      current={sortKey}
                      dir={sortDir}
                      onSort={sortRows}
                    />
                  </>
                )}
                <SortHeader
                  label={categoryLabel}
                  sortKey="category"
                  current={sortKey}
                  dir={sortDir}
                  onSort={sortRows}
                />
                <SortHeader
                  label="Today"
                  sortKey="today"
                  current={sortKey}
                  dir={sortDir}
                  onSort={sortRows}
                />
                <SortHeader
                  label="Status"
                  sortKey="status"
                  current={sortKey}
                  dir={sortDir}
                  onSort={sortRows}
                />
                {linkable && (
                  <th scope="col" className="table-header">
                    Linked account
                  </th>
                )}
                <th scope="col" className="table-header w-px">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((resource) => (
                <LinkedRow key={resource.id} to={`/app/resources/${resource.id}`}>
                  <td className="table-cell">
                    <Link
                      to={`/app/resources/${resource.id}`}
                      className="table-link"
                    >
                      {resource.name}
                    </Link>
                    {resource.soldDate && (
                      <span className="ml-2 text-xs text-gray-500">
                        sold {formatDMY(resource.soldDate)}
                      </span>
                    )}
                  </td>
                  {multiKind && (
                    <td className="table-cell text-gray-600">{KIND_LABEL[resource.kind]}</td>
                  )}
                  {isAssets && (
                    <>
                      <td className="table-cell table-muted">{resource.identifier ?? '—'}</td>
                      <td className="table-cell table-muted">
                        {resource.registrationNumber ?? '—'}
                        {/* The plate and its expiry belong in one cell: a
                            number with no indication that it lapsed last month
                            is the thing this column exists to prevent. */}
                        {resource.registrationExpiryStatus !== 'none' &&
                          resource.registrationExpiryStatus !== 'ok' && (
                            <span className="ml-2 align-middle">
                              <StatusBadge
                                kind="expiry"
                                status={resource.registrationExpiryStatus}
                              />
                            </span>
                          )}
                      </td>
                    </>
                  )}
                  <td className="table-cell table-muted">{resource.category ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge
                      kind="today"
                      status={statusToday(resource, bookedIds, awayIds)}
                    />
                  </td>
                  <td className="table-cell">
                    <StatusBadge kind="resource" status={resource.status} />
                  </td>
                  {linkable && (
                    <td className="table-cell table-muted">
                      {resource.linkedUserId
                        ? (users.find((u) => u.id === resource.linkedUserId)?.name ?? 'Linked')
                        : '—'}
                    </td>
                  )}
                  <td className="table-cell">
                    <ActionMenu
                      label={`Actions for ${resource.name}`}
                      items={[
                        {
                          label: 'Open',
                          onClick: () => {
                            window.location.href = `/app/resources/${resource.id}`;
                          },
                        },
                      ]}
                    />
                  </td>
                </LinkedRow>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {adding && (
        <NewResourceModal
          group={group}
          users={users}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function NewResourceModal({
  group,
  users,
  onClose,
  onCreated,
}: {
  group: ResourceGroup;
  users: User[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  // The group decides the kind. Only Assets offers a choice, and only between
  // its own two — a person cannot be created from the Suppliers list, which is
  // the whole point of splitting the page up.
  const [kind, setKind] = useState<ResourceKind>(group.defaultKind);
  const [category, setCategory] = useState('');
  const [linkedUserId, setLinkedUserId] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [registrationExpiry, setRegistrationExpiry] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const isAsset = kind === 'plant' || kind === 'vehicle';

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await scheduling.createResource({
        name,
        kind,
        category: category === '' ? null : category,
        linkedUserId: kind === 'person' && linkedUserId !== '' ? linkedUserId : null,
        // Sent only when the kind can hold them. The server would drop them
        // anyway — this is so the request says what the form showed.
        ...(isAsset
          ? {
              identifier: identifier === '' ? null : identifier,
              registrationNumber: registrationNumber === '' ? null : registrationNumber,
              registrationExpiry: registrationExpiry === '' ? null : registrationExpiry,
            }
          : {}),
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not add that resource.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Add to ${group.label}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="new-resource"
            disabled={busy}
            className="btn-primary btn-sm"
          >
            {busy ? 'Adding…' : 'Add resource'}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      {/* `legacy-page` scopes the transition layer to this form, which still
          uses the shared Field component's bare label/input markup. */}
      <form id="new-resource" onSubmit={submit} noValidate className="legacy-page">
        <Field
          label="Name"
          name="name"
          value={name}
          onChange={setName}
          error={fields.name}
          hint="“Jack Miller”, or “5T Excavator (KOB-05)”."
        />

        {/* One kind in the group means no question to ask. Assets is the
            only group with a choice, and it is a real one: a vehicle carries a
            plate and a person reading the list expects to see it. */}
        {group.kinds.length > 1 && (
          <label htmlFor="kind">
            Kind
            <select
              id="kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as ResourceKind)}
            >
              {group.kinds.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
        )}

        {isAsset && (
          <>
            <label htmlFor="identifier">
              ID
              <input
                id="identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="EX-05"
              />
              <span className="hint">
                Optional. Your own label for the machine; it shows next to the name wherever
                this appears. Leave it blank if you don't number your gear.
              </span>
            </label>

            <label htmlFor="registrationNumber">
              Registration
              <input
                id="registrationNumber"
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                placeholder="XT17AZ"
              />
            </label>

            <label htmlFor="registrationExpiry">
              Registration expiry
              <input
                id="registrationExpiry"
                type="date"
                value={registrationExpiry}
                onChange={(e) => setRegistrationExpiry(e.target.value)}
              />
              <span className="hint">
                Flagged on this list before it lapses, the same way a document's expiry is.
              </span>
            </label>
          </>
        )}

        {/* No `list=`/`<datalist>` (round 37, Nathan's word). The browser
            draws a dropdown ARROW for a datalist whether or not it has
            anything to offer, so a field labelled free text wore a control
            that promised a menu and — on a new company, with no categories
            recorded yet — opened nothing. The suggestions go with it. */}
        <label htmlFor="category">
          {kind === 'person' ? 'Position' : 'Category'}
          <input
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder={kind === 'person' ? 'Excavator operator, Foreman…' : 'Excavator, Tipper…'}
          />
          <span className="hint">Free text, used for grouping and filtering.</span>
        </label>

        {kind === 'person' && (
          <label htmlFor="linkedUserId">
            Linked account
            <select
              id="linkedUserId"
              value={linkedUserId}
              onChange={(e) => setLinkedUserId(e.target.value)}
            >
              <option value="">Not linked</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
            <span className="hint">
              Linking lets that person see their own assignments in the app. Crew without accounts
              schedule exactly the same way; they just don't see it.
            </span>
          </label>
        )}
      </form>
    </Modal>
  );
}
